import { useParams, useSearchParams } from '@direct-frontend/modules-router';
import { Form } from '@adv-frontend/form-kit';
import { isOrderEditable, useGetOrderQuery } from '@/entities/order';
import { AccountAvatar } from '@/features/AccountAvatar';
import { AliceRelatedServices } from '@/features/AliceRelatedServices';
import { CheckoutLogo } from '@/features/CheckoutLogo';
import { ORDER_FORM_ID, OrderForm, orderForm, PaymentInformer, toFormValues } from '@/features/OrderForm';
import { DEFAULT_ORDER, type OrderDto } from '@/shared/api/order';
import { Footer } from '@/shared/components/Footer';
import { Layout } from '@/shared/components/Layout';
import { buildShoppingCartUrl } from '@/shared/lib/buildShoppingCartUrl';
import { useInitialRender } from '@/shared/lib/hooks';
import { getRequestContext } from '@/shared/lib/requestContext';
import { CheckoutFeedback } from '@/widgets/CheckoutFeedback';
import { useCheckRegistrationOzon } from '../../lib/hooks/useCheckRegistrationOzon';
import { Modals } from '../Modals';
import { OrderNotFound } from '../OrderNotFound';
import {
    getCheckoutFormContext,
    useCheckoutModals,
    useLoyaltyOnboarding,
    usePayboxSubmit,
    usePriceChangedToast,
    useTrackCheckout,
} from './lib';
import styles from './Checkout.module.css';

interface CheckoutFormProps {
    order: OrderDto;
    signature: string;
    storeSlug: string;
    refetch: () => Promise<unknown>;
}

const CheckoutContent = ({ order, signature, storeSlug, refetch }: CheckoutFormProps) => {
    const { company } = getRequestContext();
    const { companyShopUrl } = company;

    const { modals } = useCheckoutModals();

    usePriceChangedToast();
    useLoyaltyOnboarding(order.loyalty_v2);

    const openRegistrationOzon = useCheckRegistrationOzon({
        signature,
        storeSlug,
        orderId: order.id,
    });

    const formContext = getCheckoutFormContext({
        orderId: order.id,
        signature,
        storeSlug,
        companyShopUrl: companyShopUrl || '',
        order,
        refetch,
    });

    const { handlePayboxSubmit, payboxUrl } = usePayboxSubmit({
        orderId: order.id,
        signature,
    });

    return (
        <Form id={ORDER_FORM_ID} form={orderForm} initialValue={toFormValues(order)} context={formContext}>
            <Layout
                accountSlot={<AccountAvatar />}
                logoSlot={<CheckoutLogo />}
                companyShopUrl={buildShoppingCartUrl(company)}
                sidebarSlot={<PaymentInformer order={order} handlePayboxSubmit={handlePayboxSubmit} />}
                footerSlot={<Footer hideOnDesktop centered className={styles.footer} />}
            >
                <OrderForm
                    deliveryChunks={order.delivery_chunks ?? []}
                    openRegistrationOzon={openRegistrationOzon}
                    bottomAddon={
                        <AliceRelatedServices
                            signature={signature}
                            orderId={order.id}
                            storeSlug={storeSlug}
                            relatedServiceInfo={order.related_services_info}
                        />
                    }
                />
            </Layout>
            <Modals
                order={order}
                signature={signature}
                storeSlug={storeSlug}
                modals={modals}
                payboxUrl={payboxUrl}
                openRegistrationOzon={openRegistrationOzon}
            />
        </Form>
    );
};

export const Checkout = () => {
    const { orderId = '', storeSlug = '' } = useParams();
    const { company, signature: requestContextSignature } = getRequestContext();
    const [searchParams] = useSearchParams();
    const signature = searchParams.get('signature') || requestContextSignature || '';
    const stockChanged = searchParams.get('stockChanged') ?? false;

    const {
        data: order,
        isFetching,
        refetch,
    } = useGetOrderQuery({ order_id: orderId, signature, store_slug: storeSlug });

    const isMounted = useInitialRender();

    useTrackCheckout({ order });

    if ((!isFetching && !order && !stockChanged) || !signature) {
        return <OrderNotFound />;
    }

    if (!isMounted || (!order && !stockChanged)) {
        return null;
    }

    if (order && !isOrderEditable(order)) {
        return (
            <Layout
                accountSlot={<AccountAvatar />}
                logoSlot={<CheckoutLogo />}
                companyShopUrl={buildShoppingCartUrl(company)}
            >
                <CheckoutFeedback order={order} />
            </Layout>
        );
    }

    return (
        <CheckoutContent order={order || DEFAULT_ORDER} signature={signature} storeSlug={storeSlug} refetch={refetch} />
    );
};
